import { lazy, Suspense, type ComponentType } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { OnboardingGuard } from '@/components/onboarding';
import { AppShell } from '@/components/shell';
import { WebAppGuard } from '@/components/maintenance/WebAppGuard';
import { OnboardingLayout } from '@/pages/onboarding/OnboardingLayout';
import { Spinner } from '@/components/ui';
import { MarketingRoot } from './MarketingRoot';
import { RouteMeta } from './RouteMeta';
import { flattenRoutePaths } from './routePaths';

function lazyPage(load: () => Promise<{ default: ComponentType }>) {
  const Lazy = lazy(load);
  return (
    <Suspense
      fallback={
        <div className="flex h-full min-h-[40vh] items-center justify-center">
          <Spinner size="md" label="Loading" />
        </div>
      }
    >
      <Lazy />
    </Suspense>
  );
}

const TodayPage = () => lazyPage(() => import('@/pages/TodayPage'));
const InboxPage = () => lazyPage(() => import('@/pages/InboxPage'));
const RoutinesPage = () => lazyPage(() => import('@/pages/RoutinesPage'));
const RoutineNewPage = () => lazyPage(() => import('@/pages/RoutineNewPage'));
const RoutineEditPage = () => lazyPage(() => import('@/pages/RoutineEditPage'));
const RoutineRunPage = () => lazyPage(() => import('@/pages/RoutineRunPage'));
const HabitsPage = () => lazyPage(() => import('@/pages/HabitsPage'));
const CalendarPage = () => lazyPage(() => import('@/pages/CalendarPage'));
const SettingsPage = () => lazyPage(() => import('@/pages/SettingsPage'));

const RouteErrorPage = () => lazyPage(() => import('@/pages/ErrorPage'));
const NotFound = () =>
  lazyPage(() => import('@/pages/ErrorPage').then((m) => ({ default: m.NotFoundPage })));

const LoginPage = () => lazyPage(() => import('@/pages/LoginPage'));
const PricingPage = () => lazyPage(() => import('@/pages/PricingPage'));
const HowItWorksPage = () => lazyPage(() => import('@/pages/HowItWorksPage'));
const NoShameHabitsPage = () => lazyPage(() => import('@/pages/NoShameHabitsPage'));
const ComingSoonPage = () => lazyPage(() => import('@/pages/ComingSoonPage'));
const PrivacyPage = () => lazyPage(() => import('@/pages/legal/PrivacyPage'));
const TermsPage = () => lazyPage(() => import('@/pages/legal/TermsPage'));

const WelcomePage = () => lazyPage(() => import('@/pages/onboarding/WelcomePage'));
const ScheduleStylePage = () => lazyPage(() => import('@/pages/onboarding/ScheduleStylePage'));
const EnergyPatternPage = () => lazyPage(() => import('@/pages/onboarding/EnergyPatternPage'));
const ConnectPage = () => lazyPage(() => import('@/pages/onboarding/ConnectPage'));
const FirstRoutinePage = () => lazyPage(() => import('@/pages/onboarding/FirstRoutinePage'));

const devRoutes = import.meta.env.DEV
  ? [
      { path: '/dev/components', element: lazyPage(() => import('@/pages/dev/ComponentsPage')) },
      { path: '/dev/stores', element: lazyPage(() => import('@/pages/dev/StoresPage')) },
      { path: '/dev/timeline', element: lazyPage(() => import('@/pages/dev/TimelinePage')) },
      { path: '/dev/scaffolds', element: lazyPage(() => import('@/pages/dev/ScaffoldsPage')) },
      { path: '/dev/ai-test', element: lazyPage(() => import('@/pages/dev/AiTestPage')) },
    ]
  : [];

const appRoutes: RouteObject[] = [
  // Public, awareness-only marketing + legal surface — always reachable on web.
  { path: '/', element: <MarketingRoot /> },
  { path: '/coming-soon', element: <ComingSoonPage /> },
  { path: '/privacy', element: <PrivacyPage /> },
  { path: '/terms', element: <TermsPage /> },
  { path: '/pricing', element: <PricingPage /> },
  { path: '/how-it-works', element: <HowItWorksPage /> },
  { path: '/habits', element: <NoShameHabitsPage /> },
  { path: '/auth', element: <Navigate to="/login" replace /> },
  {
    // Pre-launch web lockdown: real sign-in / sign-up and the web app are
    // disabled on the web until the App Store launch (these routes redirect to
    // /coming-soon). Native builds + preview-token holders pass through.
    element: <WebAppGuard />,
    children: [
      { path: '/login', element: <LoginPage /> },
      {
        element: <OnboardingGuard />,
        children: [
          {
            path: '/onboarding',
            element: <OnboardingLayout />,
            children: [
              { path: 'welcome', element: <WelcomePage /> },
              { path: 'schedule-style', element: <ScheduleStylePage /> },
              { path: 'energy-pattern', element: <EnergyPatternPage /> },
              { path: 'connect', element: <ConnectPage /> },
              { path: 'first-routine', element: <FirstRoutinePage /> },
            ],
          },
          {
            path: '/app',
            element: <AppShell />,
            children: [
              { index: true, element: <TodayPage />, handle: { topbar: 'day-strip' } },
              { path: 'inbox', element: <InboxPage /> },
              { path: 'routines', element: <RoutinesPage /> },
              { path: 'routines/new', element: <RoutineNewPage /> },
              { path: 'routines/:id/edit', element: <RoutineEditPage /> },
              { path: 'routines/:id/run', element: <RoutineRunPage />, handle: { chrome: 'none' } },
              { path: 'habits', element: <HabitsPage /> },
              { path: 'calendar', element: <CalendarPage /> },
              { path: 'settings', element: <SettingsPage /> },
            ],
          },
        ],
      },
    ],
  },
  // DEV-only galleries/playgrounds — public in dev, tree-shaken out of prod.
  ...devRoutes,
  // Catch-all: unknown URLs get the branded not-found page, not the router's
  // default developer boundary.
  { path: '*', element: <NotFound /> },
];

export const router = createBrowserRouter([
  {
    // Root wrapper: RouteMeta keeps the document <title> per route (renders an
    // Outlet for the children below), and ONE errorElement catches render
    // errors, loader errors, and failed lazy chunks (stale tab after a
    // redeploy) app-wide.
    element: <RouteMeta />,
    errorElement: <RouteErrorPage />,
    children: appRoutes,
  },
]);

if (import.meta.env.DEV && typeof window !== 'undefined') {
  // DEV-only seam: expose the app's real, flattened route table so the
  // screenshot harness can auto-detect routes instead of hardcoding them.
  // Tree-shaken out of production builds (import.meta.env.DEV === false).
  (window as unknown as { __BD_ROUTES?: string[] }).__BD_ROUTES = flattenRoutePaths(appRoutes);
}
